import React from 'react';
import PropTypes from 'prop-types';

import { Logger } from 'aws-amplify';

import { 
  Typography,
} from '@material-ui/core';

import { 
  makeStyles ,
  withStyles,
} from '@material-ui/core/styles';

import {
  Assignment as SubmittedIcon,
  SettingsApplications as AttemptIcon,
} from '@material-ui/icons';

import {
  Timeline,
  TimelineItem as MuiTimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@material-ui/lab';

import {
  ComponentGrouper,
  DetailPanel,
} from "../../../packages/detail-panel";

import { 
  fmtDataConfigurationRefs 
} from '../../workload/formatters';

import { 
  fmtIso8601AsLocalDateTime,
  fmtIso8601AsLocalDateQualifiedTime,
} from '../../utils/formatters';

import {
  ResourceRef,
} from '../../resource';

import {
  ExecutionInputChip,
  ExecutionInputError,
  ExecutionInputResultIcon,
  ExecutionInputTimings,
  ExecutionOutputs,
} from '.';

const logger = new Logger('ExecutionInputTimeline');

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '6px 6px',
    margin: 0,
  },
  content: {
    width: '100%',
    minWidth: 0,
    maxWidth: '500px',
  },
  rejected: {
  },
  succeeded: {
    backgroundColor: theme.palette.success.main,
  },
  skipped: {
    backgroundColor: theme.palette.warning.main,
  },
  failed: {
    backgroundColor: theme.palette.error.main,
  },
}));

const TimelineItem = withStyles({
  missingOppositeContent: {
    "&:before": {
      display: "none"
    }
  }
})(MuiTimelineItem);

export default function ExecutionInputTimeline(props) {
  const { executionInput, onAttemptClicked, onShowLog } = props;
  const classes = useStyles();

  const submitTime = fmtIso8601AsLocalDateTime(executionInput.SubmitTime);
  const endTime = fmtIso8601AsLocalDateTime(executionInput.EndTime);

  const attempts = executionInput.Attempts;

  logger.debug(executionInput);
  
  const handleAttemptClicked = (attempt) => {
    if(onAttemptClicked) {
      onAttemptClicked(attempt);
    }
  };

  const handleShowLog = (index, message) => {
    if(onShowLog) {
      onShowLog(index, message);
    }
  };
  
  return (
    <React.Fragment>
      <Timeline className={classes.root}>
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot color="primary">
              <SubmittedIcon fontSize="small"/>
            </TimelineDot>
            <TimelineConnector />
          </TimelineSeparator>
          <TimelineContent className={classes.content}>
            <DetailPanel 
              name={`SUBMITTED: ${submitTime}`}
              data={executionInput}
              fields={[
                { name: "InputObject", label: "Input", component: ExecutionInputChip, data: "$" }, 
                { name: "SourceDataset", label: "From", formatter: fmtDataConfigurationRefs, 
                  component: ResourceRef, options: { type: 'Dataset', format: 'qualified', adorned: true } },
              ]}
              labelWidth="40px" fieldWidth={null}
            />
          </TimelineContent>
        </TimelineItem>
        {
          attempts?attempts.map((attempt, index) => (
            <TimelineItem key={"ti"+index}>
              <TimelineSeparator>
                <TimelineDot className={classes[attempt.Result.toLowerCase()]}>
                  <AttemptIcon fontSize="small" onClick={() => handleAttemptClicked(index)}/>
                </TimelineDot>
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <DetailPanel 
                  name={`${attempt.Result}: ${fmtIso8601AsLocalDateQualifiedTime(attempt.StartTime, executionInput.SubmitTime)} (ATTEMPT #${index+1})`}
                  data={executionInput}
                  fields={[
                    { name: "ExecutionTimings", label: "_", component: ComponentGrouper, options: {
                      components: [
                        {
                          component: ExecutionInputTimings,
                          props: {
                            value: attempt,
                            variant: 'graph',
                          }
                        },
                        {
                          component: ExecutionInputTimings,
                          props: {
                            value: attempt,
                            variant: 'table',
                          }
                        },
                        {
                          component: ExecutionInputError,
                          props: {
                            value: attempt,
                            onShowLog: (message) => handleShowLog(index, message),
                          }
                        }
                      ]}, data: "$" }, 
                  ]}
                 fieldWidth={null}
               />
              </TimelineContent>
            </TimelineItem>
          )):null
        }
       <TimelineItem>
          <TimelineSeparator>
            <TimelineDot className={classes[executionInput.Result.toLowerCase()]}>
              <ExecutionInputResultIcon value={executionInput.Result} color="inherit" fontSize="small"/>
            </TimelineDot>
          </TimelineSeparator>
          <TimelineContent className={classes.content}>
            {executionInput.Result==='PENDING' || executionInput.Result==='FAILED'?
              <Typography variant="caption" style={{lineHeight: 2.66}}>{executionInput.Result}{executionInput.Result==='PENDING'?"...":""}</Typography>
              :
              <DetailPanel 
                name={`${executionInput.Result}: ${endTime}`}
                data={executionInput}
                fields={
                  executionInput.Result==='REJECTED'?
                    [
                      { name: "Reason", label: "Reason", formatter: (r) => r.slice(-1).pop(), data: "$.Attempts[Reason]" }, 
                    ]
                  :
                    [
                      { name: "Workload.DataConfiguration.TargetDataset", label: "To", formatter: fmtDataConfigurationRefs, 
                        component: ResourceRef, options: { type: 'Dataset', format: 'qualified', adorned: true } },
                      { name: "Outputs", label: "Output", component: ExecutionOutputs, options: { variant: 'summary' } }, 
                    ]
                }
                labelWidth="50px" 
                fieldWidth={null}
              />
            }
          </TimelineContent>
        </TimelineItem>
      </Timeline>
    </React.Fragment>
  );
}

ExecutionInputTimeline.propTypes = {
  executionInput: PropTypes.object.isRequired,
  onAttemptClicked: PropTypes.func,
  onShowLog: PropTypes.func,
};

ExecutionInputTimeline.defaultProps = {
};
